import { Select } from "melco-ui";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useChannelNames } from "../../../hooks/orders/useChannelNames";
import { FusionMelcoFusionModelsChannelListItem } from "melco-shared-logic";

// The sentinel value used to represent "None" in the encoded string.
// A leading comma signals "None is selected" to the backend (e.g. ",STORE1").
const NONE_SENTINEL = ",";


type ChannelNameFilterProps = {
    value?: string[];
    onChange: (encoded: string[]) => void;
};



export const ChannelNameFilter: React.FC<ChannelNameFilterProps> = ({
    value,
    onChange,
}) => {
    const { t } = useTranslation();

    // Channels loaded from the API. Kept in local state because this
    // data is only needed by this component (no global cache needed yet).
    // YAGNI — lift to a hook/store only if other components need it.
    const [channels, setChannels] = useState<FusionMelcoFusionModelsChannelListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const channelNames = useChannelNames()

    useEffect(() => {
        // Abort controller ensures we don't update state on an unmounted
        // component if the user navigates away before the fetch completes.
        const controller = new AbortController();

        const fetchChannels = async () => {
            setLoading(true);
            try {
                const response = await channelNames()
                if (!response) {
                    // Fail visibly rather than silently — the UI will show an
                    // empty dropdown, which is confusing. Log for observability.
                    console.error(
                        `[ChannelNameFilter] Failed to load channels}`
                    );
                    return;
                }

                const data: FusionMelcoFusionModelsChannelListItem[] = response
                setChannels(data);
            } catch (err) {
                if ((err as Error).name !== "AbortError") {
                    // AbortError is expected on unmount; anything else is real.
                    console.error("[ChannelNameFilter] Unexpected fetch error:", err);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchChannels();
        return () => controller.abort();
    }, []); // No deps — fetch once on mount. Add filters here if the
    // channel list ever needs to be re-fetched dynamically.

    /**
     * Build the option list. "None" is prepended manually because it
     * is a UI concept (represents rows with no channel) and will never
     * come from the API. It uses NONE_SENTINEL ("") as its value, which
     * produces the leading comma in the encoded output.
     */
    const options = [
        {
            value: NONE_SENTINEL,
            label: t("orders_list.filter.channel_name.none"),
        },
        ...channels.map((ch) => ({
            value: ch.channel,
            label: ch.name,
        })),
    ];

    const handleChange = (selected: string[]) => {
        onChange(selected);
    };

    return (
        <Select
            mode="multiple"
            allowClear
            loading={loading}
            placeholder={t("orders_list.filter.channel_name.placeholder")}
            style={{ width: "100%", minWidth: 150 }}

            maxTagCount={1}
            options={options}
            value={(value)}
            onChange={handleChange}
        />
    );
};